/**
 * Register a newborn as a patient of her own, linked to her mother. Twins are two registrations.
 * Everything clinical about the baby (Apgar, weight, resuscitation, the newborn checks) is then
 * recorded on the baby's own record, with the newborn forms of the maternal packs.
 */
import { EXT_MOTHER, type Patient, isoToEth, newId } from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { Field } from '../components/Field.js';
import { app, clock } from '../controller.js';
import { go } from '../hooks.js';
import { t } from '../i18n.js';
import { displayName } from '../patient.js';
import { PatientChrome } from '../shell.js';

export function NewbornRegister({ motherId }: { motherId: string }) {
  const [mother, setMother] = useState<Patient | null>(null);
  const [sex, setSex] = useState<'' | 'female' | 'male'>('');
  const [born, setBorn] = useState(clock.now().slice(0, 16));
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void app.session?.get<Patient>('Patient', motherId).then(setMother);
  }, [motherId]);
  if (!mother) return null;
  const mGiven = mother.name?.[0]?.given ?? [];
  const defaultName = t.babyOf(mGiven[0] ?? displayName(mother));
  const save = async (e: Event) => {
    e.preventDefault();
    if (!sex) return setErr(t.chooseSex);
    if (!born || born > clock.now().slice(0, 16)) return setErr(t.birthTimeInvalid);
    if (!app.session) return;
    setBusy(true);
    try {
      const text = name.trim() || defaultName;
      const baby = {
        resourceType: 'Patient',
        id: newId(),
        active: true,
        name: [{ use: 'official', text, given: text.split(/\s+/) }],
        gender: sex,
        birthDate: born.slice(0, 10),
        extension: [
          { url: EXT_MOTHER, valueReference: { reference: `Patient/${motherId}` } },
          {
            url: 'http://hl7.org/fhir/StructureDefinition/patient-birthTime',
            valueDateTime: `${born}:00${clock.now().slice(-6)}`,
          },
        ],
        ...(mother.address ? { address: mother.address } : {}),
        ...(mother.telecom ? { telecom: mother.telecom } : {}),
      } as unknown as Patient;
      await app.session.save(baby);
      app.saved();
      go(`patient/${baby.id}`);
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
      setBusy(false);
    }
  };
  const eth = isoToEth(born.slice(0, 10));
  return (
    <>
      <PatientChrome id={motherId} here="newborn" />
      <form class="card" onSubmit={(e) => void save(e)} noValidate>
        <h3>{t.registerNewborn}</h3>
        <p class="muted">{t.registerNewbornHelp}</p>
        <div class="field">
          <span class="label">{t.sex} *</span>
          <div class="seg">
            {(['female', 'male'] as const).map((g) => (
              <label key={g} class={sex === g ? 'on' : ''}>
                <input type="radio" name="nbsex" checked={sex === g} onChange={() => setSex(g)} />
                {g === 'female' ? t.female : t.male}
              </label>
            ))}
          </div>
        </div>
        <Field label={t.bornAt} for="nbborn">
          <input
            id="nbborn"
            type="datetime-local"
            class="dt"
            max={clock.now().slice(0, 16)}
            value={born}
            onInput={(e) => setBorn(e.currentTarget.value)}
          />
        </Field>
        {eth ? (
          <p class="help">
            {eth.day}/{eth.month}/{eth.year} E.C.
          </p>
        ) : null}
        <Field label={t.babyName} for="nbname">
          <input
            id="nbname"
            placeholder={defaultName}
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </Field>
        {err ? <p class="alert">{err}</p> : null}
        <div class="actions sticky-actions">
          <button type="button" class="ghost" onClick={() => history.back()}>
            {t.cancel}
          </button>
          <button type="submit" class="primary" disabled={busy}>
            {busy ? t.saving : t.save}
          </button>
        </div>
      </form>
    </>
  );
}
